import { createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback } from 'react';
import { User, Session, Provider } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { probePrivilegedAccess } from '@/integrations/supabase/adminAuthProbe';
import { logger } from '@/lib/logger';
import { errorMessage } from '@/lib/errorMessage';
import { broadcastAuthEvent, subscribeAuthEvents } from '@/lib/sessionBroadcast';
import { endStoredAdminSessions } from '@/lib/adminSessionRegistry';
import { sessionIdentity } from '@/lib/authSessionId';
import { recordSessionActivity, sharedActivityAge } from '@/lib/sessionLifetime';

// A probe that never settles must not strand the UI in 'checking'.
const ADMIN_PROBE_TIMEOUT_MS = 8000;

const SESSION_TIMEOUT_WARNING_MS = 25 * 60 * 1000; // 25 minutes
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Where the admin-authorization probe currently stands.
 *
 * `idle` and `checking` are pending states; callers must not render a final
 * access decision until the current session's probe reaches a terminal state.
 */
export type AdminCheckState = 'idle' | 'checking' | 'admin' | 'unauthorized' | 'error';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  /** Terminal-vs-pending discriminator for the admin probe. See AdminCheckState. */
  adminCheckState: AdminCheckState;
  /** Verbatim failure text when adminCheckState === 'error'; else null. */
  adminCheckError: string | null;
  /** Re-run the probe against the current session (used by the retry button). */
  recheckAdminRole: () => Promise<void>;
  isLoading: boolean;
  showTimeoutWarning: boolean;
  dismissTimeoutWarning: () => void;
  extendSession: () => void;
  /** `captchaToken` is the Cloudflare Turnstile token from the login form; Supabase verifies it when captcha protection is on. */
  signIn: (email: string, password: string, options?: { captchaToken?: string }) => Promise<{ error: Error | null }>;
  signInWithProvider: (provider: Provider) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  const [adminCheckState, setAdminCheckState] = useState<AdminCheckState>('idle');
  const [adminCheckError, setAdminCheckError] = useState<string | null>(null);

  /**
   * Monotonic probe counter. A slow probe from an earlier session must never
   * overwrite the result of a newer one — otherwise signing in as a second
   * account can inherit the first account's authorization answer.
   */
  const probeSeq = useRef(0);
  const authGeneration = useRef(0);
  const currentSession = useRef<Session | null>(null);
  const identitySettled = useRef(false);
  const ownsAdminSession = useRef(false);

  /**
   * The in-flight probe's AbortController. A newer probe aborts the previous
   * one so superseded requests do not stack concurrent fetches (PR #422
   * review): without this, rapid re-probes leave older requests running
   * until their own race resolves.
   */
  const probeAbortRef = useRef<AbortController | null>(null);

  /**
   * Ask the database whether this caller is a privileged portal subject.
   *
   * RETURNS THE FULL RESULT, not a boolean. The previous shape collapsed every
   * outcome to `false`, which made "not an admin", "the RPC failed", and "we
   * have not finished asking" indistinguishable — so a failure rendered as a
   * silent bounce back to the login form, identical to a wrong password.
   *
   * TOKEN-BOUND. The access token is passed explicitly rather than relying on
   * the client's ambient session, so the probe answers for the session that
   * triggered it even if the global session is swapped mid-flight.
   *
   * AAL1-safe: the database returns only the boolean. Roles, organizations and
   * capabilities load after the mandatory AAL2 step-up in AdminAccessProvider.
   */
  const probeAdminRole = async (accessToken: string, signal?: AbortSignal): Promise<{
    isAdmin: boolean;
    state: AdminCheckState;
    error: string | null;
  }> => {
    try {
      // Use native fetch through the focused helper. This avoids the shared
      // PostgREST client's ambient token lookup and does not add the optional
      // organization header to the pre-MFA bootstrap request.
      const { data, error } = await probePrivilegedAccess(accessToken, signal);

      if (error) {
        logger.error('Admin authorization probe failed', error, {
          context: { source: 'useAuth.probeAdminRole' },
        });
        return {
          isAdmin: false,
          state: 'error',
          error: error.message ?? 'Access verification failed',
        };
      }
      return data === true
        ? { isAdmin: true, state: 'admin', error: null }
        : { isAdmin: false, state: 'unauthorized', error: null };
    } catch (err) {
      // An AbortError here is the EXPECTED outcome of the timeout winning the
      // race (runAdminProbe aborts the controller in its finally block), or
      // of a newer probe superseding this one. It is not a failure — log at
      // debug so expected timeouts do not spam Sentry.
      if (err instanceof DOMException && err.name === 'AbortError') {
        logger.debug('Admin authorization probe aborted (timeout or superseded)', {
          context: { source: 'useAuth.probeAdminRole' },
        });
        return {
          isAdmin: false,
          state: 'error',
          error: 'Access verification timed out. Check your connection and retry.',
        };
      }
      logger.error('Admin authorization probe threw', err, {
        context: { source: 'useAuth.probeAdminRole' },
      });
      return {
        isAdmin: false,
        state: 'error',
        error: errorMessage(err, 'Access verification failed'),
      };
    }
  };

  /**
   * Run the probe and COMMIT ALL THREE pieces of state together.
   *
   * This is the whole bug the first fix missed: probeAdminRole returned a
   * `state` field that no call site ever passed to setAdminCheckState, so the
   * state was written only with the literals 'checking' and 'idle'. It could
   * never leave 'checking', and the login page's error card — gated on
   * `state !== 'checking'` — was unreachable by construction. Verified in the
   * deployed bundle: 3x setAdminCheckState('checking'), 3x ('idle'), and zero
   * calls with 'admin' | 'unauthorized' | 'error'.
   *
   * Every write to isAdmin now goes through here so the two cannot drift.
   */
  const runAdminProbe = useCallback(async (
    targetSession: Session,
    generation = authGeneration.current,
    options: { silent?: boolean } = {},
  ) => {
    if (generation !== authGeneration.current || targetSession.access_token !== currentSession.current?.access_token) return;
    // Every probe takes a ticket, silent or not: superseding a silent
    // continuity revalidation must still invalidate it.
    const seq = ++probeSeq.current;
    if (!options.silent) {
      ownsAdminSession.current = false;
      setIsAdmin(false);
      setAdminCheckState('checking');
      setAdminCheckError(null);
    }

    // Supersede any in-flight probe: abort it so its fetch actually stops
    // instead of lingering until its own race resolves (PR #422 review).
    probeAbortRef.current?.abort();
    const abortController = new AbortController();
    probeAbortRef.current = abortController;

    // A hung probe must not strand the UI in 'checking' forever. Without this,
    // a request that never settles reproduces the original symptom exactly:
    // no redirect (isAdmin false) and no error card (state still 'checking').
    //
    // PR #353/#354 remediation: the timeout is cleared when the probe settles
    // first (no leaked timers across repeated probes), and the timed-out
    // request is actually ABORTED via AbortController instead of lingering
    // in-flight (the stale-probe guard only ignores the result).
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<{ isAdmin: boolean; state: AdminCheckState; error: string | null }>(
      (resolve) => {
        timeoutId = setTimeout(
          () => resolve({
            isAdmin: false,
            state: 'error',
            error: 'Access verification timed out. Check your connection and retry.',
          }),
          ADMIN_PROBE_TIMEOUT_MS,
        );
      },
    );

    let result: { isAdmin: boolean; state: AdminCheckState; error: string | null };
    try {
      result = await Promise.race([
        probeAdminRole(targetSession.access_token, abortController.signal),
        timeout,
      ]);
    } finally {
      // Clear the timer if the probe won the race; abort the request if the
      // timeout won. Exactly one of the two applies per probe. The abort is
      // a no-op when a newer probe already aborted this controller.
      if (timeoutId !== null) clearTimeout(timeoutId);
      abortController.abort();
      if (probeAbortRef.current === abortController) probeAbortRef.current = null;
    }

    // Stale-response guard: a newer probe has already answered.
    if (seq !== probeSeq.current || generation !== authGeneration.current) return;

    // A transient failure of a SILENT revalidation (error, timeout, network)
    // is not an answer: keep the last verified admin identity so the mounted
    // page survives. Only a definitive result (admin / unauthorized) is
    // applied. The first, non-silent check stays fail-closed.
    if (options.silent && result.state === 'error') {
      logger.warn('Silent admin revalidation failed; keeping last verified identity', {
        context: { source: 'useAuth.runAdminProbe', error: result.error },
      });
      return;
    }

    ownsAdminSession.current = result.isAdmin;
    setIsAdmin(result.isAdmin);
    setAdminCheckState(result.state);
    setAdminCheckError(result.error);
    // The concrete generation guard above proves this is the session that is
    // still on screen, so its identity is now established.
    identitySettled.current = true;
    // Only the winning probe may release bootstrap loading. A superseded
    // getSession probe must not expose isAdmin=false while its successor runs.
    setIsLoading(false);
  }, []);

  /** Clear authorization state without running a probe. */
  const clearAdminState = useCallback(() => {
    probeSeq.current += 1; // invalidate anything in flight
    probeAbortRef.current?.abort(); // and actually cancel its fetch
    probeAbortRef.current = null;
    ownsAdminSession.current = false;
    setIsAdmin(false);
    setAdminCheckState('idle');
    setAdminCheckError(null);
  }, []);

  const beginSession = useCallback((next: Session | null) => {
    const generation = ++authGeneration.current;
    currentSession.current = next;
    identitySettled.current = false;
    clearAdminState();
    setSession(next);
    setUser(next?.user ?? null);
    setIsLoading(!!next?.user);
    setShowTimeoutWarning(false);
    setLastActivity(Date.now());
    return generation;
  }, [clearAdminState]);

  const recheckAdminRole = useCallback(async () => {
    if (!currentSession.current) return;
    const generation = authGeneration.current;
    clearAdminState();
    setIsLoading(true);
    setAdminCheckState('checking');
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (generation !== authGeneration.current) return;
      if (!s?.user) { beginSession(null); return; }
      if (s.access_token !== currentSession.current?.access_token) {
        const nextGeneration = beginSession(s);
        await runAdminProbe(s, nextGeneration);
      } else await runAdminProbe(s, generation);
    } catch (error) {
      if (generation !== authGeneration.current) return;
      setIsAdmin(false);
      ownsAdminSession.current = false;
      setAdminCheckState('error');
      setAdminCheckError('Access verification failed. Check your connection and retry.');
      setIsLoading(false);
      logger.error('Admin access retry failed', error);
    }
  }, [runAdminProbe, clearAdminState, beginSession]);

  const updateActivity = useCallback(() => {
    recordSessionActivity();
    setLastActivity(Date.now());
    setShowTimeoutWarning(false);
  }, []);

  const dismissTimeoutWarning = useCallback(() => {
    setShowTimeoutWarning(false);
  }, []);

  const extendSession = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  // Session timeout logic — admin-only. Other portals (Employee, Member,
  // Client) run their OWN idle timer via useEnterpriseSessionGuard /
  // MemberLayout. If we ran this timer for any signed-in user, the root
  // AuthProvider would silently wipe employee/member/client sessions
  // after 30 min of mouse-idle, broadcasting a logout that races the
  // portal's own auth state and corrupts the shared refresh token.
  useEffect(() => {
    if (!session || !isAdmin) return;

    const checkTimeout = () => {
      // The session is shared, so activity in any other tab or portal counts:
      // this logout ends all of them.
      const timeSinceActivity = Math.min(Date.now() - lastActivity, sharedActivityAge() ?? Infinity);

      if (timeSinceActivity >= SESSION_TIMEOUT_MS) {
        // Idle timeout — broadcast to other tabs first so they all
        // tear down together, then revoke server-side via signOut().
        broadcastAuthEvent({ type: 'logout', reason: 'idle' });
        // Mark this browser's "Active Sessions" row inactive while the
        // session can still write it, then sign out (2026-10-13 wave 2;
        // the sidebar's Sign out already does this). This is a timer
        // callback, not onAuthStateChange, so the awaited write cannot hit
        // the auth-js#762 lock. endStoredAdminSessions is bounded and never
        // throws; the catch is belt and braces so sign-out always runs.
        // Fire-and-forget: the SIGNED_OUT event (not the returned promise)
        // is what tears down local state.
        const generation = authGeneration.current;
        void endStoredAdminSessions()
          .catch(() => undefined)
          .then(() => {
            if (generation === authGeneration.current && ownsAdminSession.current) {
              beginSession(null);
              return supabase.auth.signOut();
            }
          });
      } else if (timeSinceActivity >= SESSION_TIMEOUT_WARNING_MS) {
        setShowTimeoutWarning(true);
      } else {
        setShowTimeoutWarning(false);
      }
    };

    const interval = setInterval(checkTimeout, 30000); // Check every 30 seconds

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    return () => {
      clearInterval(interval);
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, [session, isAdmin, lastActivity, updateActivity, beginSession]);

  useEffect(() => {
    const initialGeneration = authGeneration.current;
    // Listener first. Invalidate old probes NOW, before the deferred work runs.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      const previousIdentity = sessionIdentity(currentSession.current);
      if (
        (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') &&
        nextSession?.user &&
        identitySettled.current &&
        nextSession.user.id === currentSession.current?.user.id &&
        previousIdentity !== null &&
        previousIdentity === sessionIdentity(nextSession)
      ) {
        // Routine rotation of the established session. Keep isAdmin, the
        // loading gate and the mounted page untouched; adopt the token and
        // revalidate authorization in the background so revocations land.
        currentSession.current = nextSession;
        setSession(nextSession);
        setUser(nextSession.user);
        setTimeout(() => {
          void runAdminProbe(nextSession, authGeneration.current, { silent: true });
        }, 0);
        return;
      }
      const generation = beginSession(nextSession);
      if (event === 'TOKEN_REFRESHED' && !nextSession) {
        logger.warn('Admin token refresh failed; clearing local session');
        return;
      }
      if (nextSession?.user) {
        setTimeout(() => { void runAdminProbe(nextSession, generation); }, 0);
      }
    });
    void supabase.auth.getSession().then(({ data: { session: existing } }) => {
      if (initialGeneration !== authGeneration.current) return;
      const generation = beginSession(existing);
      if (existing?.user) void runAdminProbe(existing, generation);
    }).catch((error) => {
      if (initialGeneration !== authGeneration.current) return;
      beginSession(null);
      logger.error('Admin session bootstrap failed', error);
    });

    const unsubscribeBroadcast = subscribeAuthEvents((msg) => {
      if (msg.type !== 'logout') return;
      const wasAdmin = ownsAdminSession.current;
      beginSession(null);
      // Only the root provider's owning role revokes the shared session.
      if (wasAdmin) void supabase.auth.signOut().catch((error) => logger.warn('Admin cross-tab signout failed', error));
    });
    return () => {
      authGeneration.current++;
      currentSession.current = null;
      identitySettled.current = false;
      probeSeq.current++;
      probeAbortRef.current?.abort();
      subscription.unsubscribe();
      unsubscribeBroadcast();
    };
  }, [runAdminProbe, clearAdminState, beginSession]);

  const signIn = async (email: string, password: string, options?: { captchaToken?: string }) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken: options?.captchaToken },
    });
    return { error };
  };

  const signInWithProvider = async (provider: Provider) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/admin`,
      },
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const signOut = async () => {
    broadcastAuthEvent({ type: 'logout', reason: 'manual' });
    beginSession(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      isAdmin, 
      adminCheckState,
      adminCheckError,
      recheckAdminRole,
      isLoading, 
      showTimeoutWarning,
      dismissTimeoutWarning,
      extendSession,
      signIn, 
      signInWithProvider,
      signUp, 
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
